import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * AutoUpdater
 *  Silent background watcher that polls the backend's /api/version
 *  every 30s. When the build_id changes (i.e. a new deploy happened),
 *  it shows a tiny "🧪 Updating…" toast for 800 ms and then hard-reloads
 *  the page so installed PWAs pick up the latest code without the user
 *  ever needing to clear cache.
 *
 *  Also calls navigator.serviceWorker.getRegistration().update() on the
 *  same interval as a belt-and-braces for the SW layer.
 */
const POLL_MS = 30_000;
const SESSION_KEY = "seanlab_build_id";

export default function AutoUpdater() {
  const [updating, setUpdating] = useState(false);
  const initialRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const checkOnce = async () => {
      if (cancelled || reloadingRef.current) return;
      try {
        const res = await fetch(`${API}/version`, {
          cache: "no-store",
          headers: { "cache-control": "no-cache" },
        });
        if (!res.ok) return;
        const data = await res.json();
        const fresh = data.build_id;
        if (!fresh) return;

        // First successful response: remember the build we loaded with.
        if (initialRef.current === null) {
          initialRef.current = fresh;
          sessionStorage.setItem(SESSION_KEY, fresh);
          return;
        }

        if (fresh !== initialRef.current) {
          reloadingRef.current = true;
          setUpdating(true);
          // Also prod the service worker to swap in the new shell
          try {
            if ("serviceWorker" in navigator) {
              const reg = await navigator.serviceWorker.getRegistration();
              if (reg) reg.update().catch(() => {});
            }
          } catch {/* noop */}
          // Tiny visual confirmation before reload
          setTimeout(() => {
            // bust HTTP cache on reload by appending a no-op query param
            const u = new URL(window.location.href);
            u.searchParams.set("_v", fresh.slice(0, 8));
            window.location.replace(u.toString());
          }, 900);
        }
      } catch {
        // Network errors are fine — try again next interval.
      }
    };

    // First check after a short delay (let the app paint first)
    const t0 = setTimeout(checkOnce, 4000);
    const id = setInterval(checkOnce, POLL_MS);
    // Re-check immediately when tab/PWA gains focus (Android home-screen reopens)
    const onFocus = () => checkOnce();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkOnce();
    });

    return () => {
      cancelled = true;
      clearTimeout(t0);
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!updating) return null;
  return (
    <div
      className="fixed z-[70] no-print"
      data-testid="auto-updater-toast"
      style={{
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "0.6rem 1rem",
        background: "var(--ink)",
        color: "#fff",
        borderRadius: 999,
        fontSize: "0.85rem",
        fontFamily: '"JetBrains Mono", monospace',
        boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
      }}
    >
      🧪 Updating to the latest lab build…
    </div>
  );
}
